import { useEffect, useMemo } from "react";
import { ResponsiveContainer, BarChart, Bar, CartesianGrid, XAxis, YAxis, Tooltip } from "recharts";

import TimeSeriesChart, { type TimeSeriesSeries } from "../../../components/charts/TimeSeriesChart";
import type { Viewport, Extent } from "../../../lib/chartViewport";
import type { CountriesRow } from "./hooks";
import type { ChartSeries, CombinedSample } from "./charting";
import { formatDate } from "./utils";

export type DashboardTab = "curves" | "countries" | "articles";

type CurvePanelsProps = {
  activeTab: DashboardTab;
  onTabChange: (tab: DashboardTab) => void;
  tooltipFontSize: number;
  sensorSeries: ChartSeries[];
  expeditionSeries: ChartSeries[];
  sensorChartData: CombinedSample[];
  expeditionChartData: CombinedSample[];
  sensorDomain?: readonly [number, number];
  expeditionDomain?: readonly [number, number];
  trackingRange: { start: Date | null; end: Date | null };
  isTrackingLoading: boolean;
  topCountries: CountriesRow[];
  countriesLoading: boolean;
  articleHistogram: Array<{ article: string; count: number }>;
  articlesLoading: boolean;
  extent: Extent | null;     // ms epoch
  viewport: Viewport | null; // ms epoch
  onViewportChange: (next: Viewport) => void;
};

export function BlulogCharts(props: CurvePanelsProps) {
  const {
    activeTab,
    onTabChange,
    tooltipFontSize,
    sensorSeries,
    expeditionSeries,
    sensorChartData,
    expeditionChartData,
    sensorDomain,
    expeditionDomain,
    trackingRange,
    isTrackingLoading,
    topCountries,
    countriesLoading,
    articleHistogram,
    articlesLoading,
    extent,
    viewport,
    onViewportChange,
  } = props;

  const showTrackingRange = trackingRange.start && trackingRange.end;

  // ✅ Normalise timestamp en ms + tri seulement si nécessaire
  const sensorDataSorted = useMemo(
    () => normalizeAndSortByTimestampMs(sensorChartData),
    [sensorChartData]
  );
  const expeditionDataSorted = useMemo(
    () => normalizeAndSortByTimestampMs(expeditionChartData),
    [expeditionChartData]
  );

  // ✅ Viewport init sans spread / sans gros tableaux temporaires
  useEffect(() => {
    if (viewport) return;

    const mm1 = minMaxTimestampMs(sensorDataSorted);
    const mm2 = minMaxTimestampMs(expeditionDataSorted);
    if (!mm1 && !mm2) return;

    let minT = Number.POSITIVE_INFINITY;
    let maxT = Number.NEGATIVE_INFINITY;

    if (mm1) {
      minT = Math.min(minT, mm1.min);
      maxT = Math.max(maxT, mm1.max);
    }
    if (mm2) {
      minT = Math.min(minT, mm2.min);
      maxT = Math.max(maxT, mm2.max);
    }

    if (!Number.isFinite(minT) || !Number.isFinite(maxT) || minT === maxT) return;

    const pad = Math.max(1, Math.round((maxT - minT) * 0.02));
    onViewportChange({ start: minT - pad, end: maxT + pad });
  }, [viewport, sensorDataSorted, expeditionDataSorted, onViewportChange]);

  const sensorSeriesMeta = useMemo(
    () => sensorSeries.map(s => ({ id: s.id, label: s.label, color: s.color })),
    [sensorSeries]
  );
  const expeditionSeriesMeta = useMemo(
    () => expeditionSeries.map(s => ({ id: s.id, label: s.label, color: s.color })),
    [expeditionSeries]
  );

  // ✅ Build séries en 1 passe (beaucoup moins de GC)
  const sensorSeriesData = useMemo<TimeSeriesSeries[]>(
    () => buildTimeSeriesSeriesFast(sensorSeriesMeta, sensorDataSorted),
    [sensorSeriesMeta, sensorDataSorted],
  );
  const expeditionSeriesData = useMemo<TimeSeriesSeries[]>(
    () => buildTimeSeriesSeriesFast(expeditionSeriesMeta, expeditionDataSorted),
    [expeditionSeriesMeta, expeditionDataSorted],
  );

  return (
    <section className="blulog-charts">
      {/* ... ton JSX inchangé ... */}
      <div className="blulog-tabs">
        <button type="button" className={activeTab === "curves" ? "is-active" : ""} onClick={() => onTabChange("curves")}>
          Courbes
        </button>
        <button type="button" className={activeTab === "countries" ? "is-active" : ""} onClick={() => onTabChange("countries")}>
          Pays
        </button>
        <button type="button" className={activeTab === "articles" ? "is-active" : ""} onClick={() => onTabChange("articles")}>
          Articles
        </button>
      </div>

      <div className="blulog-tabpanels">
        {activeTab === "curves" ? (
          <div>
            <div className="blulog-panel">
              <header className="blulog-panel-header">
                <h4>Température des capteurs</h4>
                {showTrackingRange ? (
                  <p>
                    {formatDate(trackingRange.start)} → {formatDate(trackingRange.end)}
                  </p>
                ) : null}
              </header>

              {sensorSeriesData.length && sensorDataSorted.length ? (
                <TimeSeriesChart
                  series={sensorSeriesData}
                  height={500}
                  yLabel="Température (°C)"
                  viewport={viewport}
                  onViewportChange={onViewportChange}
                  extent={extent}
                  yDomain={sensorDomain ?? undefined}
                  unitLabel="°C"
                  tooltipFontSize={tooltipFontSize}
                  showLegend
                />
              ) : (
                <p className="blulog-empty">
                  {isTrackingLoading ? "Chargement des courbes…" : "Sélectionnez un numéro de suivi pour afficher les courbes."}
                </p>
              )}
            </div>

            {sensorSeriesMeta.length && expeditionSeriesMeta.length ? <hr className="blulog-divider" /> : null}

            {expeditionSeriesMeta.length ? (
              <div className="blulog-panel">
                <header className="blulog-panel-header">
                  <h4>Température des expéditions</h4>
                  <p>Courbes par expédition (jeux de données rejoués).</p>
                </header>

                {expeditionSeriesData.length ? (
                  <TimeSeriesChart
                    series={expeditionSeriesData}
                    height={500}
                    yLabel="Température (°C)"
                    viewport={viewport}
                    onViewportChange={onViewportChange}
                    extent={extent}
                    yDomain={expeditionDomain ?? undefined}
                    unitLabel="°C"
                    tooltipFontSize={tooltipFontSize}
                    showLegend
                  />
                ) : (
                  <p className="blulog-empty">Aucune donnée d&apos;expédition à afficher.</p>
                )}
              </div>
            ) : null}
          </div>
        ) : null}

        {/* onglets pays/articles inchangés */}
        {/* ... */}
      </div>
    </section>
  );
}

function toMs(ts: number): number {
  // si ts ressemble à des secondes UNIX (10 chiffres), on convertit en ms
  return ts < 2e10 ? ts * 1000 : ts;
}

function normalizeAndSortByTimestampMs(rows: CombinedSample[]): CombinedSample[] {
  const out: CombinedSample[] = [];
  let isSorted = true;
  let prev = Number.NEGATIVE_INFINITY;

  for (let i = 0; i < rows.length; i++) {
    const r = rows[i];
    const raw = r.timestamp as number;
    if (!Number.isFinite(raw)) continue;

    const t = toMs(raw);
    // on évite de muter l’objet d’origine : on clone uniquement le champ timestamp
    const rr = (t === raw) ? r : ({ ...r, timestamp: t } as CombinedSample);

    out.push(rr);
    if (t < prev) isSorted = false;
    prev = t;
  }

  if (!isSorted && out.length > 1) {
    out.sort((a, b) => (a.timestamp as number) - (b.timestamp as number));
  }
  return out;
}

function minMaxTimestampMs(rows: CombinedSample[]): { min: number; max: number } | null {
  if (!rows.length) return null;
  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;

  for (let i = 0; i < rows.length; i++) {
    const t = rows[i].timestamp as number;
    if (!Number.isFinite(t)) continue;
    if (t < min) min = t;
    if (t > max) max = t;
  }

  if (!Number.isFinite(min) || !Number.isFinite(max)) return null;
  return { min, max };
}

function buildTimeSeriesSeriesFast(
  meta: Array<{ id: string; label: string; color: string }>,
  rows: CombinedSample[]
): TimeSeriesSeries[] {
  const sCount = meta.length;
  if (sCount === 0 || rows.length === 0) return [];

  const ids = new Array<string>(sCount);
  const series: TimeSeriesSeries[] = new Array(sCount);

  for (let i = 0; i < sCount; i++) {
    const m = meta[i];
    ids[i] = m.id;
    series[i] = { id: m.id, name: m.label, color: m.color, data: [] };
  }

  for (let r = 0; r < rows.length; r++) {
    const row = rows[r];
    const t = row.timestamp as number;
    if (!Number.isFinite(t)) continue;

    for (let i = 0; i < sCount; i++) {
      const v = row[ids[i]];
      if (typeof v === "number" && Number.isFinite(v)) {
        series[i].data.push({ t, v });
      }
    }
  }

  return series;
}
